// Master input (microphone gain) and output volume, like Discord's Voice settings. Stored per device.
const IN = 'malvo-input-volume', OUT = 'malvo-output-volume';
const read = (k: string, d: number) => { try { const v = Number(localStorage.getItem(k)); return Number.isFinite(v) && localStorage.getItem(k) !== null ? v : d; } catch { return d; } };
export const inputVolume = () => Math.max(0, Math.min(200, read(IN, 100)));
export const outputVolume = () => Math.max(0, Math.min(100, read(OUT, 100)));
const gains = new Set<GainNode>();
const tracks = new Set<any>();
const pendingProcessors=new WeakMap<object,Promise<void>>();
const gateModules=new WeakMap<AudioContext,Promise<void>>();
const gateThreshold=()=>Math.pow(10,Math.max(-60,Math.min(-25,read('malvo-noise-threshold',-45)))/20);
export function setInputVolume(v: number) {
  try { localStorage.setItem(IN, String(Math.round(v))); } catch {}
  gains.forEach(g => g.gain.value = inputVolume() / 100);
  // A live mic without the gain processor (volume was 100 %) gets it now.
  for (const t of [...tracks]) { if (t.mediaStreamTrack?.readyState === 'ended') { tracks.delete(t); continue; } void applyMicGain(t); }
}
export function setOutputVolume(v: number) { try { localStorage.setItem(OUT, String(Math.round(v))); } catch {} window.dispatchEvent(new Event('malvo:output-volume')); }

let ctx: AudioContext | null = null;
function micContext() {
  if (!ctx || ctx.state === 'closed') ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' });
  if (ctx.state !== 'running') {
    void ctx.resume().catch(() => {});
    const wake = () => { void ctx?.resume().catch(() => {}); };
    window.addEventListener('pointerdown', wake, { once: true, capture: true });
    window.addEventListener('keydown', wake, { once: true, capture: true });
  }
  return ctx;
}
// LiveKit processor: browser-processed mic → audio-thread silence gate → input gain. Never connected to the speakers.
export function micGainProcessor() {
  let gate:AudioWorkletNode|null=null;
  let src: MediaStreamAudioSourceNode | null = null, gain: GainNode | null = null, dest: MediaStreamAudioDestinationNode | null = null;
  const p: any = {
    name: 'malvo-mic-gain',
    processedTrack: undefined as MediaStreamTrack | undefined,
    async init(opts: { track: MediaStreamTrack; audioContext?: AudioContext }) {
      const ac = opts.audioContext && opts.audioContext.state !== 'closed' ? opts.audioContext : micContext();
      src = ac.createMediaStreamSource(new MediaStream([opts.track]));
      gain = ac.createGain(); gain.gain.value = inputVolume() / 100; gains.add(gain);
      dest = ac.createMediaStreamDestination();
      if(ac.audioWorklet){try{let ready=gateModules.get(ac);if(!ready){ready=ac.audioWorklet.addModule('/mic-gate-v1.js');gateModules.set(ac,ready);}await ready;gate=new AudioWorkletNode(ac,'malvo-mic-gate-v1');(gate.parameters.get('threshold')!).value=gateThreshold();(gate.parameters.get('enabled')!).value=localStorage.getItem('malvo-noise-suppression')==='0'?0:1;src.connect(gate).connect(gain);}catch(error){console.warn('Microphone silence gate unavailable; browser processing remains active:',error instanceof Error?error.name:'Error');src.connect(gain);}}else src.connect(gain);
      gain.connect(dest);
      p.processedTrack = dest.stream.getAudioTracks()[0];
    },
    async restart(opts: any) { await p.destroy(); await p.init(opts); },
    async destroy() { try { src?.disconnect();gate?.disconnect();gain?.disconnect();p.processedTrack?.stop(); } catch {} if (gain) gains.delete(gain); gate=null;src = gain = dest = null; },
  };
  return p;
}
export async function applyMicGain(track: any) {
  if (!track || track.kind !== 'audio' || typeof track.setProcessor !== 'function') return;
  if(!tracks.has(track)){tracks.add(track);track.once?.('ended',()=>tracks.delete(track));}
  if (track.getProcessor?.()?.name === 'malvo-mic-gain') return;
  if(pendingProcessors.has(track))return pendingProcessors.get(track);
  const job=(async()=>{try{
    if (!track.audioContext) track.setAudioContext?.(micContext());
    await track.setProcessor(micGainProcessor());
  }catch(e){console.warn('Microphone processing unavailable:',e instanceof Error?e.name:'Error');}})();
  pendingProcessors.set(track,job);try{await job;}finally{if(pendingProcessors.get(track)===job)pendingProcessors.delete(track);}

}
export function forgetMicTrack(track: any) { tracks.delete(track); }
